/** @file Export dialog: target, page size, scale (or fit for sprite sheets), padding and verify. */
import { SCALE_MAX } from '@pose2d/api/export/scaleMax.data';
import type { Translate } from '@pose2d/i18n/types';
import type { ExportFormState } from './exportForm';
import { formSetter } from './formSetter';

const PAGES = [1024, 2048, 4096] as const;
const pageSize = (value: string): ExportFormState['maxPage'] => PAGES.find((p) => String(p) === value) ?? 2048;

/** Props shared by the field groups. */
export interface FieldsProps {
  t: Translate;
  form: ExportFormState;
  onChange: (next: ExportFormState) => void;
}

/** The scale input hides while a sprite sheet fits its frames; a rig's is the texture scale, capped at 1 (W3-61). */
export function ExportFields({ t, form, onChange }: FieldsProps) {
  const set = formSetter(form, onChange);
  const sprite = form.target === 'spritesheet';
  return (
    <div className="export-fields">
      <label>{t('exportDialog.target')}
        <select value={form.target} onChange={(e) => { set('target', e.target.value === 'spritesheet' ? 'spritesheet' : 'rig'); }}>
          <option value={'rig'}>{t('exportDialog.targetRig')}</option>
          <option value={'spritesheet'}>{t('exportDialog.targetSprites')}</option>
        </select>
      </label>
      <label>{t('exportDialog.maxPage')}
        <select value={String(form.maxPage)} onChange={(e) => { set('maxPage', pageSize(e.target.value)); }}>
          {PAGES.map((p) => <option key={p} value={String(p)}>{p}</option>)}
        </select>
      </label>
      {sprite ? <label className="export-check"><input type="checkbox" checked={form.fit} onChange={(e) => { set('fit', e.target.checked); }} />{t('exportDialog.fit')}</label> : null}
      {sprite && form.fit ? null : (
        <div className="export-scale">
          <label>{t('exportDialog.scale')}<input type="number" step={0.25} min={0.25} max={sprite ? SCALE_MAX : 1} value={form.scale} onChange={(e) => { set('scale', Number(e.target.value)); }} /></label>
          {sprite ? null : <small className="shell-hint">{t('exportDialog.rigScale')}</small>}
        </div>
      )}
      <label>{t('exportDialog.padding')}<input type="number" min={0} max={16} value={form.padding} onChange={(e) => { set('padding', Number(e.target.value)); }} /></label>
      <label className="export-check"><input type="checkbox" checked={form.verify} onChange={(e) => { set('verify', e.target.checked); }} />{t('exportDialog.verify')}</label>
    </div>
  );
}
